import { Building2, ChevronDown, Globe } from "lucide-react";
import { useTenant } from "@/data/tenancy/context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

/**
 * Tenant/company context switcher for the app shell.
 * Only visible to cross-tenant operators (super_admin, owner).
 * Normal users see a static tenant label.
 */
export function TenantSwitcher() {
  // Pilot mode: single-branch deployments — no tenant/company picker needed.
  // Re-enable when multi-branch or multi-company rollout begins.
  return null;
   
  const { activeTenantId, activeTenant, tenants, canSwitchTenant, setActiveTenantId } = useTenant();

  if (!canSwitchTenant) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-sidebar-foreground/60">
        <Building2 className="h-3.5 w-3.5" />
        <span className="truncate">{activeTenant?.name ?? "Company"}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 text-xs text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors w-full">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate flex-1 text-left">
          {activeTenantId ? activeTenant?.name : "All Companies"}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuItem
          onClick={() => setActiveTenantId(null)}
          className="flex items-center gap-2"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>All Companies</span>
          {!activeTenantId && (
            <Badge variant="secondary" className="ml-auto text-[10px] px-1.5">
              Active
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {tenants.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onClick={() => setActiveTenantId(t.id)}
            className="flex items-center gap-2"
          >
            <Building2 className="h-3.5 w-3.5" />
            <div className="flex-1 min-w-0">
              <span className="truncate block">{t.name}</span>
              <span className="text-[10px] text-muted-foreground">{t.status}</span>
            </div>
            {activeTenantId === t.id && (
              <Badge variant="secondary" className="ml-auto text-[10px] px-1.5">
                Active
              </Badge>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
